import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../../lib/api.js";
import { Plus, Trash2 } from "lucide-react";
import { inputCls } from "./_shared.jsx";

export function SlaSettingsTab() {
  const [rows, setRows] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState({ brand: "", sla_business_days: 5 });
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, b] = await Promise.all([
        api.get("/admin/sla-settings"),
        api.get("/brands"),
      ]);
      setRows(s.data);
      setBrands(b.data.filter((x) => x.active));
    } catch (e) {
      setError(e?.response?.data?.detail || "Falha ao carregar.");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const usedBrands = new Set(rows.map((r) => r.brand));
  const availableBrands = brands.filter((b) => !usedBrands.has(b.name));

  const handleCreate = async () => {
    if (!draft.brand || !draft.sla_business_days) return;
    try {
      await api.post("/admin/sla-settings", { brand: draft.brand, sla_business_days: Number(draft.sla_business_days) });
      setDraft({ brand: "", sla_business_days: 5 });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Falha ao criar.");
    }
  };

  const handleSave = async (id, next) => {
    try {
      await api.patch(`/admin/sla-settings/${id}`, next);
      setEditingId(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Falha ao guardar.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este SLA?")) return;
    try {
      await api.delete(`/admin/sla-settings/${id}`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Falha ao eliminar.");
    }
  };

  if (loading) return <div className="text-sm text-[#6C757D]" data-testid="sla-loading">A carregar…</div>;

  return (
    <div data-testid="sla-settings-tab">
      <div className="mb-3 text-sm text-[#6C757D]">
        Define o SLA (em dias úteis) para obras de cada marca. Marcas sem SLA explícito usam o defeito de <span className="font-mono">5 dias úteis</span>. O KPI <span className="font-semibold">% dentro do SLA</span> no Dashboard usa estes valores.
      </div>

      {error && <div className="mb-3 px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm" data-testid="sla-error">{error}</div>}

      <table className="w-full text-sm bg-white border border-gray-200 rounded-md overflow-hidden">
        <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-widest text-[#6C757D]">
          <tr>
            <th className="text-left px-3 py-2">Marca</th>
            <th className="text-right px-3 py-2">SLA (dias úteis)</th>
            <th className="text-right px-3 py-2 w-32">Acções</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const editing = editingId === r.id;
            return (
              <tr key={r.id} className="border-t border-gray-100" data-testid={`sla-row-${r.brand}`}>
                <td className="px-3 py-2">{r.brand}</td>
                <td className="px-3 py-2 text-right font-mono">
                  {editing ? (
                    <input
                      type="number"
                      step="0.5"
                      min="0.5"
                      defaultValue={r.sla_business_days}
                      onChange={(e) => (r._draft = Number(e.target.value))}
                      className="border border-gray-300 rounded-sm px-2 py-0.5 w-20 text-right"
                      data-testid={`sla-input-${r.brand}`}
                    />
                  ) : `${r.sla_business_days} d`}
                </td>
                <td className="px-3 py-2 text-right">
                  {editing ? (
                    <>
                      <button onClick={() => handleSave(r.id, { sla_business_days: r._draft ?? r.sla_business_days })} className="text-xs px-2 py-1 bg-[#1F3864] text-white rounded-sm mr-1" data-testid={`sla-save-${r.brand}`}>Guardar</button>
                      <button onClick={() => setEditingId(null)} className="text-xs px-2 py-1 border border-gray-300 rounded-sm">Cancelar</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => setEditingId(r.id)} className="text-xs text-[#1F3864] hover:underline mr-3" data-testid={`sla-edit-${r.brand}`}>Editar</button>
                      <button onClick={() => handleDelete(r.id)} className="text-xs text-[#DC3545] hover:underline" data-testid={`sla-delete-${r.brand}`}>Eliminar</button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
          {availableBrands.length > 0 && (
            <tr className="border-t border-gray-100 bg-[#F4F6F8]/50">
              <td className="px-3 py-2">
                <select value={draft.brand} onChange={(e) => setDraft({ ...draft, brand: e.target.value })} className="border border-gray-300 rounded-sm px-2 py-1 w-full" data-testid="sla-new-brand">
                  <option value="">— escolher marca —</option>
                  {availableBrands.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
                </select>
              </td>
              <td className="px-3 py-2 text-right">
                <input type="number" step="0.5" min="0.5" value={draft.sla_business_days} onChange={(e) => setDraft({ ...draft, sla_business_days: e.target.value })} className="border border-gray-300 rounded-sm px-2 py-1 w-24 text-right font-mono" data-testid="sla-new-days" />
              </td>
              <td className="px-3 py-2 text-right">
                <button onClick={handleCreate} disabled={!draft.brand} className="text-xs px-3 py-1 bg-[#1F3864] text-white rounded-sm disabled:opacity-50" data-testid="sla-create-btn">Adicionar</button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="mt-2 text-[11px] text-[#94A3B8]">{rows.length} SLA(s) configurado(s). Marcas sem SLA usam 5 dias úteis.</div>
    </div>
  );
}

